/**
 * BecomesTheManySkillEffect — Conjuration Swarm path apotheosis.
 *
 * Conjures a self-sustaining swarm of 7 wisps with coordinated
 * suppression AI for 25 seconds. Dead wisps auto-replace every
 * 3 seconds during the duration window. At maximum wisp count,
 * wisps shift to `encircle` AI mode: distribute evenly around the
 * enemy cluster centroid at 2-tile radius, facing inward, closing
 * gaps when enemies flee. On duration end, all wisps vanish.
 *
 * Mechanic:
 * - On activate: spawn 7 wisps (apotheosis baseline, overrides
 *   Overwhelm's 5 and any resolver maxWisps below 7).
 * - During 25-second window: any wisp combat death queues a 3-second
 *   auto-replace timer. When the timer fires, spawn one replacement
 *   wisp at the player's position (cap enforced at spawn time).
 * - At max count (7): wisps use `aiMode: 'encircle'` instead of
 *   `'coordinated'`.
 * - On duration end: `SummonSystem.destroyAll()` clears all wisps.
 *
 * Stacking with Relentless: both systems independently enqueue
 * respawn timers for the same combat death. `maxWisps` clamps the
 * result — the first timer to fire fills the cap; the second is
 * silently skipped.
 *
 * Very long cooldown tier (4000ms). Extends ToggleSkillEffect for
 * active/cooldown lifecycle (same pattern as StormOfBladesSkillEffect).
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Becomes the Many
 */

import { ToggleSkillEffect } from '../../ToggleSkillEffect.js';
import type { WispSummonConfig } from './SummonSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface BecomesTheManyDeps {
  readonly resolver: {
    getMaxEffect(skillId: string, key: string): number | undefined;
    getMultiplicativeEffect(skillId: string, key: string, base?: number): number;
  };
  readonly player: {
    getPosition(): { x: number; y: number };
  };
  readonly summons: {
    spawn(config: WispSummonConfig, x: number, y: number): string;
    /** Returns the number of currently living wisps. */
    getWispCount(): number;
    /** Destroy all wisps. Called on duration end. */
    destroyAll(): void;
  };
  readonly targeting: {
    /** Returns true if the freshness signal should reduce effectiveness. */
    shouldFizzle(): boolean;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface BecomesTheManyConfig {
  readonly cooldownMs: number;
  /** Duration of the sustained swarm mode in ms. */
  readonly durationMs: number;
  /** Apotheosis-tier wisp cap baseline. */
  readonly maxWisps: number;
  /** Delay between a combat death and the auto-replacement spawn. */
  readonly autoReplaceIntervalMs: number;
  /** Base HP for apotheosis-tier wisps. */
  readonly baseHp: number;
  /** Aggro weight for apotheosis-tier wisps. */
  readonly baseAggroWeight: number;
  /** Base drift speed for wisps. */
  readonly baseDriftSpeed: number;
  /** Leash radius in tiles. */
  readonly leashRadiusTiles: number;
}

const DEFAULT_CONFIG: BecomesTheManyConfig = {
  cooldownMs: 4000,              // very_long cooldown tier
  durationMs: 25000,             // 25-second sustained swarm
  maxWisps: 7,                   // apotheosis baseline (overrides Overwhelm's 5)
  autoReplaceIntervalMs: 3000,   // 3-second replacement delay
  baseHp: 22,                    // wisp HP (same tier as Overwhelm)
  baseAggroWeight: 0.8,
  baseDriftSpeed: 1.0,
  leashRadiusTiles: 8,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class BecomesTheManySkillEffect extends ToggleSkillEffect {
  readonly skillId = 'becomes_the_many';

  private readonly deps: BecomesTheManyDeps;
  private readonly config: BecomesTheManyConfig;

  // Active-state timers
  private _durationRemaining = 0;

  /**
   * Per-death auto-replace timers. Each element is the remaining ms
   * until that replacement wisp spawns. Independent timers — multiple
   * concurrent combat deaths produce multiple independent countdowns.
   */
  private _replaceTimers: number[] = [];

  // Observable state
  private _resolvedMaxWisps = 0;
  private _initialSpawnCount = 0;
  private _autoReplaceCount = 0;
  private _skippedReplaceCount = 0;
  private _activationCount = 0;
  private _totalSpawnIds: string[] = [];

  constructor(deps: BecomesTheManyDeps, config: Partial<BecomesTheManyConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get durationRemaining(): number { return this._durationRemaining; }
  get resolvedMaxWisps(): number { return this._resolvedMaxWisps; }
  get initialSpawnCount(): number { return this._initialSpawnCount; }
  get autoReplaceCount(): number { return this._autoReplaceCount; }
  get skippedReplaceCount(): number { return this._skippedReplaceCount; }
  get activationCount(): number { return this._activationCount; }
  get pendingReplaceTimerCount(): number { return this._replaceTimers.length; }
  get totalSpawnIds(): readonly string[] { return this._totalSpawnIds; }

  // --- ToggleSkillEffect interface ---

  canActivate(): boolean {
    return this.cooldownRemaining <= 0 && !this._isActive;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle: apotheosis activation fails entirely on fizzle
    if (this.deps.targeting.shouldFizzle()) return false;

    // Resolve max wisp cap: apotheosis baseline (7) overrides anything lower.
    const maxWispsOverride = this.deps.resolver.getMaxEffect('swarm', 'maxWisps');
    const maxWisps = maxWispsOverride !== undefined
      ? Math.max(this.config.maxWisps, maxWispsOverride)
      : this.config.maxWisps;
    this._resolvedMaxWisps = maxWisps;

    // Resolve HP multiplier via summon scope
    const hpMultiplier = this.deps.resolver.getMultiplicativeEffect('summon', 'hpMultiplier', 1.0);
    const resolvedHp = Math.round(this.config.baseHp * hpMultiplier);

    const pos = this.deps.player.getPosition();
    const currentCount = this.deps.summons.getWispCount();
    const toSpawn = Math.max(0, maxWisps - currentCount);

    const spawnIds: string[] = [];
    for (let i = 0; i < toSpawn; i++) {
      const wispConfig: WispSummonConfig = {
        id: `btm_wisp_${this._activationCount}_${i}`,
        archetype: 'wisp',
        baseHp: resolvedHp,
        baseDamage: 0,
        baseSpeed: this.config.baseDriftSpeed,
        leashRadiusTiles: this.config.leashRadiusTiles,
        aggroWeight: this.config.baseAggroWeight,
        aiMode: toSpawn >= maxWisps ? 'encircle' : 'coordinated',
      };
      const id = this.deps.summons.spawn(wispConfig, pos.x, pos.y);
      spawnIds.push(id);
    }

    this._initialSpawnCount = toSpawn;
    this._totalSpawnIds = [...this._totalSpawnIds, ...spawnIds];
    this._durationRemaining = this.config.durationMs;
    this._replaceTimers = [];
    this._isActive = true;
    this._activationCount++;

    return true;
  }

  /**
   * Called by the integration layer when a wisp dies during the
   * apotheosis window. Only combat deaths trigger auto-replacement.
   *
   * @param _wispId   The dying wisp's ID (not tracked — all combat
   *                  deaths trigger a timer regardless of wisp identity).
   * @param deathCause  'combat' | 'detonate' | 'expiry' | 'converge'
   */
  onWispDied(
    _wispId: string,
    deathCause: 'combat' | 'detonate' | 'expiry' | 'converge',
  ): void {
    if (!this._isActive) return;
    if (deathCause !== 'combat') return;

    this._replaceTimers.push(this.config.autoReplaceIntervalMs);
  }

  // --- ToggleSkillEffect lifecycle ---

  protected onDeactivate(): void {
    // Duration expired or manual deactivation — destroy all wisps
    this.deps.summons.destroyAll();
    this._durationRemaining = 0;
    this._replaceTimers = [];
    this.cooldownRemaining = this.config.cooldownMs;
  }

  protected updateWhileActive(delta: number): void {
    // Decrement duration
    this._durationRemaining = Math.max(0, this._durationRemaining - delta);
    if (this._durationRemaining <= 0) {
      this.deactivate();
      return;
    }

    // Tick auto-replace timers
    if (this._replaceTimers.length > 0) {
      const stillPending: number[] = [];
      for (const remaining of this._replaceTimers) {
        const newRemaining = remaining - delta;
        if (newRemaining > 0) {
          stillPending.push(newRemaining);
        } else {
          // Timer fired — attempt to spawn a replacement wisp
          const wispCount = this.deps.summons.getWispCount();
          if (wispCount < this._resolvedMaxWisps) {
            const pos = this.deps.player.getPosition();
            const hpMultiplier = this.deps.resolver.getMultiplicativeEffect('summon', 'hpMultiplier', 1.0);
            const resolvedHp = Math.round(this.config.baseHp * hpMultiplier);

            const wispConfig: WispSummonConfig = {
              id: `btm_replace_${this._autoReplaceCount}`,
              archetype: 'wisp',
              baseHp: resolvedHp,
              baseDamage: 0,
              baseSpeed: this.config.baseDriftSpeed,
              leashRadiusTiles: this.config.leashRadiusTiles,
              aggroWeight: this.config.baseAggroWeight,
              // Use encircle if at (or near) max after this spawn
              aiMode: (wispCount + 1) >= this._resolvedMaxWisps ? 'encircle' : 'coordinated',
            };

            const id = this.deps.summons.spawn(wispConfig, pos.x, pos.y);
            this._totalSpawnIds.push(id);
            this._autoReplaceCount++;
          } else {
            this._skippedReplaceCount++;
          }
        }
      }
      this._replaceTimers = stillPending;
    }
  }

  destroy(): void {
    this._durationRemaining = 0;
    this._replaceTimers = [];
    super.destroy();
  }
}
